import { useEffect, useRef } from "react";
import { SessionData } from "./types";
import { confirmSession } from "./api";

interface Props {
  data: SessionData;
  onDone: () => void;
}

export function ConfirmationStep({ data, onDone }: Props) {
  // Fire confirmSession exactly once when this step appears (mirrors iOS,
  // where it fires on Done — we do it on appear so the email/Slack ping
  // happens even if the user closes the tab).
  const fired = useRef(false);
  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    if (data.sessionId != null && data.sessionToken != null) {
      confirmSession({ sessionId: data.sessionId, token: data.sessionToken }).catch(() => {
        /* swallow — the session is already saved, this just triggers notifications */
      });
    }
  }, [data.sessionId, data.sessionToken]);

  const docCount = data.selectedDocuments.length;

  return (
    <div className="wizard-step wizard-confirmation">
      <div className="wizard-success-icon" aria-hidden="true">✅</div>

      <h2>You're All Set!</h2>
      <p className="wizard-muted">
        {data.documentsSkipped
          ? "Your request is pending — upload your documents when you're ready."
          : "Your notarization request has been received."}
      </p>

      <div className="wizard-summary-card">
        <SummaryRow icon="👤" label={data.name} />
        <SummaryRow icon="✉️" label={data.email} />
        <SummaryRow icon="📞" label={data.phone} />
        <SummaryRow
          icon="📄"
          label={
            data.documentsSkipped
              ? "No documents uploaded yet"
              : `${docCount} document${docCount === 1 ? "" : "s"} uploaded`
          }
        />
      </div>

      <div className="wizard-callout">
        <div className="wizard-callout-title">🔔 What happens next</div>
        <p>
          {data.documentsSkipped
            ? "Email your documents to hello@nynotary.io whenever you're ready. Once we receive them, your request will be moved to Documents Ready and a licensed NY notary will reach out to schedule your session."
            : `A licensed NY State notary will contact you at ${data.email} or ${data.phone} within a few hours to schedule your remote session.`}
        </p>
      </div>

      <button type="button" className="wizard-button-primary" onClick={onDone}>
        Done
      </button>
    </div>
  );
}

function SummaryRow({ icon, label }: { icon: string; label: string }) {
  return (
    <div className="wizard-summary-row">
      <span aria-hidden="true">{icon}</span>
      <span>{label}</span>
    </div>
  );
}
